'use client';

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import classnames from 'classnames';

import { Popover, PopoverContent, PopoverTrigger } from '../../popover/Popover';
import { Input } from '../input/Input';
import { FieldDropdownIcon } from '../layout/FieldDropdownIcon';
import { MultiselectOptions } from '../layout/MultiselectOptions';
import type { SelectOption } from '../layout/SelectOption.types';
import { SelectOptions } from '../layout/SelectOptions';
import type { MultiselectAutocompleteProps } from './MultiselectAutocomplete.types';

import styles from './MultiselectAutocomplete.module.scss';

export function MultiselectAutocomplete({
  style,
  className,
  optionLabels,
  onSelect,
  onChange,
  onAddOption,
  options,
  disabled = false,
  error = false,
  placeholder = 'Select...',
  testId = 'ui-multiselect-autocomplete',
}: MultiselectAutocompleteProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const openOnFocusRef = useRef(true);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [userModifiedValues, setUserModifiedValues] = useState<
    SelectOption[] | null
  >(null);

  const autocompleteId = useId();
  const listboxId = `${autocompleteId}-listbox`;
  const inputId = `${autocompleteId}-input`;

  const selectedOptionsFromProps = useMemo(
    () =>
      options
        .filter((option) => option.selected)
        .map(({ value, label, customLabel }) => ({
          value,
          label,
          customLabel,
        })),
    [options],
  );

  useEffect(() => {
    setUserModifiedValues((currentLocalValues) => {
      if (selectedOptionsFromProps.length === 0) {
        return null;
      }
      if (!currentLocalValues) {
        return selectedOptionsFromProps;
      }
      const localValues = new Set(currentLocalValues.map((opt) => opt.value));
      const newSelections = selectedOptionsFromProps.filter(
        (opt) => !localValues.has(opt.value),
      );
      if (newSelections.length > 0) {
        return [...currentLocalValues, ...newSelections];
      }
      return currentLocalValues;
    });
  }, [selectedOptionsFromProps]);

  const values = userModifiedValues ?? selectedOptionsFromProps;

  const filteredOptions = useMemo(() => {
    const notSelected = options.filter(
      (option) =>
        !option.disabled && !values.some((v) => v.value === option.value),
    );
    if (!inputValue || typeof inputValue !== 'string') {
      return notSelected;
    }
    const searchTerm = inputValue.toLowerCase();
    return notSelected.filter((option) =>
      option.label.toLowerCase().includes(searchTerm),
    );
  }, [options, values, inputValue]);

  const shouldShowAddOption = useMemo(
    () =>
      !!(
        onAddOption &&
        inputValue &&
        !options.some(
          (option) => option.label.toLowerCase() === inputValue.toLowerCase(),
        )
      ),
    [onAddOption, inputValue, options],
  );

  const dispatchChangeEvent = useCallback(() => {
    containerRef.current?.dispatchEvent(new Event('change', { bubbles: true }));
  }, []);

  const handleRemoveSelectedValue = useCallback(
    (value: string | number) => {
      const newValues = values.filter((v) => v.value !== value);
      setUserModifiedValues(newValues);
      onSelect(newValues.map((v) => v.value));
      dispatchChangeEvent();
    },
    [values, onSelect, dispatchChangeEvent],
  );

  const handleOptionSelect = useCallback(
    (option: SelectOption) => {
      if (option.disabled || values.some((v) => v.value === option.value)) {
        return;
      }
      const newValues = [
        ...values,
        {
          value: option.value,
          label: option.label,
          customLabel: option.customLabel,
        },
      ];
      setUserModifiedValues(newValues);
      onSelect(newValues.map((v) => v.value));
      dispatchChangeEvent();
      setInputValue('');
      openOnFocusRef.current = false;
      inputRef.current?.focus();
      openOnFocusRef.current = true;
      setIsOpen(false);
    },
    [values, onSelect, dispatchChangeEvent],
  );

  const handleAddNewOption = useCallback(() => {
    if (!inputValue || !onAddOption) return;

    onAddOption(inputValue);
    setInputValue('');
    dispatchChangeEvent();
  }, [inputValue, onAddOption, dispatchChangeEvent]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      setIsOpen(true);
      onChange?.(newValue);
    },
    [onChange],
  );

  const handleInputFocus = useCallback(() => {
    if (!disabled && openOnFocusRef.current) setIsOpen(true);
  }, [disabled]);

  const handleInputClick = useCallback(() => {
    if (!disabled) setIsOpen(true);
  }, [disabled]);

  const handleDropdownIconClick = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => {
        if (!prev) inputRef.current?.focus();
        return !prev;
      });
    }
  }, [disabled]);

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      strategy="fixed"
      matchReferenceWidth
      dismissOnAncestorScroll
    >
      <div
        ref={containerRef}
        style={style}
        className={classnames(styles['multiselect-autocomplete'], className)}
        data-testid={testId}
      >
        <PopoverTrigger asChild>
          <Input
            ref={inputRef}
            id={inputId}
            type="text"
            className={styles['input']}
            placeholder={placeholder}
            value={inputValue}
            onFocus={handleInputFocus}
            onChange={handleInputChange}
            onClick={handleInputClick}
            disabled={disabled}
            error={error}
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-multiselectable="true"
          />
        </PopoverTrigger>
        <FieldDropdownIcon
          disabled={disabled}
          error={error}
          onClick={handleDropdownIconClick}
        />
      </div>

      {values.length > 0 && (
        <div
          className={styles['selected-items']}
          role="list"
          aria-label="Selected items"
        >
          <MultiselectOptions
            testId={`${testId}_selected`}
            options={values}
            disabled={disabled}
            onClick={(option: SelectOption) =>
              handleRemoveSelectedValue(option.value)
            }
          />
        </div>
      )}
      {isOpen && (
        <PopoverContent initialFocus={-1} style={{ padding: 0 }}>
          <SelectOptions
            testId={`${testId}_options`}
            options={filteredOptions}
            onClick={handleOptionSelect}
            onAddOption={shouldShowAddOption ? handleAddNewOption : undefined}
            labels={optionLabels}
            id={listboxId}
            role="listbox"
            aria-labelledby={inputId}
          />
        </PopoverContent>
      )}
    </Popover>
  );
}
